import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ExternalLink, Send, Sparkles, GraduationCap, Star, Search as SearchIcon, X, Clock } from "lucide-react";
import Comments from "@/components/Comments";

const FIELDS = ["ALL", "Teknologi", "Kesehatan", "Sosial-Humaniora", "Ekonomi", "Pendidikan", "Sains", "Multidisiplin"];
const INDEX = ["ALL", "Scopus Q1", "Scopus Q2", "Scopus Q3", "Scopus Q4", "Sinta 2", "Sinta 3", "Sinta 4"];

const badgeColor = (idx) => {
  if (idx.startsWith("Scopus")) return "bg-blue-600";
  if (idx.startsWith("Sinta 2")) return "bg-emerald-600";
  if (idx.startsWith("Sinta 3")) return "bg-amber-500";
  return "bg-violet-600";
};

export default function Journals() {
  const [items, setItems] = useState([]);
  const [portals, setPortals] = useState([]);
  const [preview, setPreview] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [indexing, setIndexing] = useState(params.get("indexing") || "ALL");
  const [field, setField] = useState(params.get("field") || "ALL");
  const [gated, setGated] = useState(null);
  const [favIds, setFavIds] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [history, setHistory] = useState([]);
  const nav = useNavigate();
  const fromPaper = params.get("from") === "paper";

  const loadFavorites = async () => {
    try { const { data } = await http.get("/journals/favorites"); setFavIds(new Set(data.ids || [])); } catch {}
  };
  const loadHistory = async () => {
    try { const { data } = await http.get("/journals/search-history"); setHistory(data || []); } catch {}
  };

  const load = async (qOverride) => {
    try {
      const queryValue = qOverride !== undefined ? qOverride : q;
      const { data } = await http.get("/journals", { params: { indexing, field, q: queryValue || undefined } });
      setItems(data); setGated(false);
      // save history (if there is any filter)
      if ((queryValue || "").trim() || indexing !== "ALL" || field !== "ALL") {
        http.post("/journals/search-history", { query: queryValue || "", indexing, field, result_count: data.length }).then(loadHistory).catch(()=>{});
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setGated(true);
        try { const { data } = await http.get("/journals/preview"); setPreview(data); } catch {}
      } else setGated(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line
  useEffect(() => { load(); }, [indexing, field]); // eslint-disable-line
  useEffect(() => { http.get("/portals").then(r => setPortals(r.data)); loadFavorites(); loadHistory(); }, []);

  const toggleFav = async (jid) => {
    try {
      const { data } = await http.post(`/journals/${jid}/favorite`);
      setFavIds(prev => {
        const n = new Set(prev);
        if (data.favorited) n.add(jid); else n.delete(jid);
        return n;
      });
    } catch {}
  };

  const applyHistory = (h) => {
    setQ(h.query || "");
    setIndexing(h.indexing || "ALL");
    setField(h.field || "ALL");
  };

  const clearHistory = async () => {
    await http.delete("/journals/search-history");
    setHistory([]);
  };

  const visibleItems = useMemo(() => (showFavoritesOnly ? items.filter(x => favIds.has(x.id)) : items), [items, favIds, showFavoritesOnly]);

  if (gated) {
    return (
      <div className="max-w-4xl mx-auto py-10" data-testid="journals-gate">
        <div className="text-center">
          <Badge className="bg-[#D97706] text-white border-0 mb-3"><Sparkles size={12} className="mr-1"/> Fitur Premium</Badge>
          <h1 className="serif text-3xl sm:text-4xl font-bold text-[#0F2922]">Buka Akses Jurnal Premium</h1>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            65+ jurnal terindeks <b>Scopus Q1–Q4</b> & <b>SINTA 2/3/4</b> lengkap dengan tombol submit langsung ke portal jurnalnya. Berikut 3 contoh yang menanti Anda:
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {preview.map((j, i) => (
            <div key={i} className="kt-card p-5 relative overflow-hidden group hover:shadow-lg transition" data-testid="journal-preview-card">
              <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-[#FAFA96]/70 blur-2xl group-hover:scale-125 transition"/>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-600 text-white border-0"><Star size={10} className="mr-0.5"/> {j.indexing}</Badge>
                </div>
                <h3 className="font-semibold text-[#0F2922] mt-2 serif text-lg leading-snug">{j.name}</h3>
                <div className="text-xs text-slate-500 mt-1">{j.publisher}</div>
                <p className="text-xs text-slate-600 mt-3 line-clamp-3">{j.scope}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="text-slate-400 uppercase tracking-widest">{j.field}</span>
                  <span className="text-[#D97706] font-semibold">🔒 Detail lengkap +submit</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#0F2922] via-[#173E34] to-[#0F2922] text-[#FAFA96] text-center shadow-xl border border-[#0F2922]">
          <GraduationCap size={36} className="mx-auto text-[#D97706]"/>
          <h2 className="serif text-2xl font-bold mt-2 text-[#FAFA96]">Ingin lihat 62 jurnal lainnya?</h2>
          <p className="mt-2 text-[#FAFA96]/85 max-w-lg mx-auto text-sm">Dukungan Anda mulai <b className="text-white">Rp50.000</b> membantu kami memperluas kurasi jurnal Scopus/SINTA, pemeliharaan server AI, dan pengembangan fitur baru untuk mahasiswa Indonesia.</p>
          <Button onClick={() => nav("/donasi")} className="mt-5 bg-[#D97706] hover:bg-[#B45309] text-white h-11 px-8 font-semibold" data-testid="gate-donate-btn">
            jika ingin melanjutkan, klik disini
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">Direktori Jurnal</p>
      <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">Scopus Q1–Q4 · SINTA 2/3/4</h1>
      <p className="text-slate-600 mt-2">60+ jurnal terkurasi dengan tombol submit langsung ke portal jurnal.</p>

      {fromPaper && (
        <div className="mt-4 rounded-xl p-4 bg-[#FAFA96]/40 border border-[#D97706]/30 flex items-start gap-3" data-testid="from-paper-banner">
          <Sparkles size={18} className="text-[#D97706] shrink-0 mt-0.5"/>
          <div className="flex-1 text-sm text-slate-700">
            Anda baru saja membuat paper. Pilih jurnal di bawah yang paling cocok dengan topikmu, lalu klik <b>Submit</b> untuk membuka portal pengiriman jurnalnya.
          </div>
          <button onClick={() => { params.delete("from"); setParams(params); }} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {portals.map(p => (
          <a key={p.name} href={p.url} target="_blank" rel="noreferrer" className="text-xs bg-[#0F2922] text-[#FAFA96] px-3 py-1.5 rounded-full hover:bg-[#173E34]">{p.name}</a>
        ))}
      </div>

      <div className="mt-6 kt-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
          <Input
            placeholder="Cari nama jurnal, penerbit, bidang, atau topik (contoh: machine learning, kesehatan, UI, Elsevier)…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-10 pr-10 h-11"
            data-testid="journal-search-input"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="journal-search-clear">
              <X size={16}/>
            </button>
          )}
        </div>
        <Select value={indexing} onValueChange={setIndexing}>
          <SelectTrigger className="w-44 h-11" data-testid="journal-filter-indexing"><SelectValue /></SelectTrigger>
          <SelectContent>{INDEX.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={field} onValueChange={setField}>
          <SelectTrigger className="w-44 h-11" data-testid="journal-filter-field"><SelectValue /></SelectTrigger>
          <SelectContent>{FIELDS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* quick filter chips */}
      <div className="mt-3 flex flex-wrap gap-2">
        {["Scopus Q1", "Scopus Q2", "Sinta 2", "Sinta 3"].map(chip => (
          <button
            key={chip}
            onClick={() => setIndexing(chip)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${indexing === chip ? "bg-[#D97706] text-white border-[#D97706]" : "bg-white border-slate-200 hover:border-[#D97706] text-slate-700"}`}
            data-testid={`chip-${chip.replace(" ", "-")}`}
          >
            {chip}
          </button>
        ))}
        <button
          onClick={() => setShowFavoritesOnly(s => !s)}
          className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1 ${showFavoritesOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white border-slate-200 hover:border-amber-400 text-slate-700"}`}
          data-testid="chip-favorites"
        >
          <Star size={11} className={showFavoritesOnly ? "fill-white" : ""}/> Favorit {favIds.size > 0 && <Badge className="ml-1 bg-white text-amber-600 border-0 text-[10px] px-1.5">{favIds.size}</Badge>}
        </button>
        {(q || indexing !== "ALL" || field !== "ALL" || showFavoritesOnly) && (
          <button onClick={() => { setQ(""); setIndexing("ALL"); setField("ALL"); setShowFavoritesOnly(false); }} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-red-400 text-red-600" data-testid="reset-filters">
            Reset filter
          </button>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-3 flex items-center flex-wrap gap-2" data-testid="search-history-row">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
            <Clock size={11}/> Pencarian terakhir:
          </div>
          {history.slice(0, 6).map((h, i) => {
            const label = [h.query, h.indexing !== "ALL" ? h.indexing : null, h.field !== "ALL" ? h.field : null].filter(Boolean).join(" · ") || "(semua)";
            return (
              <button
                key={i}
                onClick={() => applyHistory(h)}
                className="text-xs px-3 py-1 rounded-full bg-[#FAFA96]/60 border border-[#D97706]/20 text-[#0F2922] hover:bg-[#FAFA96]"
                data-testid={`history-chip-${i}`}
              >
                {label}
              </button>
            );
          })}
          <button onClick={clearHistory} className="text-[11px] text-slate-400 hover:text-red-600 underline" data-testid="clear-history-btn">bersihkan</button>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <span>Menampilkan <b className="text-[#0F2922]">{visibleItems.length}</b> jurnal{q ? ` untuk "${q}"` : ""}{showFavoritesOnly ? " (favorit saja)" : ""}</span>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {visibleItems.map(j => {
          const isFav = favIds.has(j.id);
          return (
          <div key={j.id} className="kt-card p-5" data-testid="journal-card-item">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-[#0F2922]">{j.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{j.publisher}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => toggleFav(j.id)}
                  aria-label="Favorit"
                  data-testid={`fav-btn-${j.id}`}
                  className={`p-2 rounded-lg transition ${isFav ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}
                  title={isFav ? "Hapus dari favorit" : "Tambah ke favorit"}
                >
                  <Star size={18} className={isFav ? "fill-amber-500" : ""}/>
                </button>
                <Badge className={`${badgeColor(j.indexing)} text-white`}>{j.indexing}</Badge>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">{j.field}</Badge>
              <Badge variant="outline" className="border-[#D97706] text-[#D97706]">APC: {j.apc}</Badge>
            </div>
            <div className="mt-4 flex gap-2">
              <a href={j.url_home} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-1 text-[#0F2922] hover:underline">
                Beranda jurnal <ExternalLink size={14}/>
              </a>
              <a href={j.url_submit} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-sm font-semibold bg-[#D97706] hover:bg-[#B45309] text-white px-3 py-1.5 rounded-lg" data-testid="journal-submit-btn">
                <Send size={14}/> Submit
              </a>
            </div>
            <Comments contentType="journal" contentId={j.id} />
          </div>
          );
        })}
        {visibleItems.length === 0 && (
          <div className="col-span-full text-center py-12">
            <SearchIcon size={40} className="mx-auto text-slate-300" strokeWidth={1.2}/>
            <p className="mt-3 text-slate-500">
              {showFavoritesOnly
                ? "Belum ada jurnal favorit. Klik ⭐ pada kartu jurnal untuk menyimpannya."
                : <>Tidak ada jurnal yang cocok dengan pencarian <b>"{q}"</b>.</>}
            </p>
            {!showFavoritesOnly && <button onClick={() => { setQ(""); setIndexing("ALL"); setField("ALL"); }} className="text-sm text-[#D97706] font-semibold mt-2 underline">Reset filter</button>}
          </div>
        )}
      </div>
    </div>
  );
}
